import { InvestorType } from 'lib/enums/investor';
import {
  InvestorPrivateWhole,
  ManageInvestorPayload,
} from 'lib/interfaces/database/investor-private';
import { UserDoc } from 'lib/interfaces/database/user';
import _ from 'lodash';
import { InvestorsService } from 'services/investors';
import { create } from 'zustand';

interface Filters {
  type: InvestorType;
}

interface State {
  data: InvestorPrivateWhole[];
  filters: Partial<Filters>;
}

interface Actions {
  readonly load: (force?: boolean) => void;
  readonly create: (v: ManageInvestorPayload) => Promise<boolean>;
  readonly update: (id: string, v: ManageInvestorPayload) => Promise<boolean>;

  readonly setFilters: (v?: Partial<Filters>) => void;
  readonly createUser: (
    investorID: string,
    v: Partial<UserDoc>
  ) => Promise<boolean>;
}

interface Store extends State, Actions {}

const useInvestorsStore = create<Store>((set, get) => {
  const service = InvestorsService.getInstance();

  return {
    data: [],

    filters: {},

    load: async (force) => {
      if (!force && get().data.length > 0) {
        console.debug('Skipped unforced load');
        return;
      }

      const results = await service.search({});

      if (results) {
        set({ data: results });
      }
    },

    create: async (v) => {
      const record = await service.create(v);

      if (record) {
        const nextData = _.clone(get().data);
        nextData.push(record);

        set({ data: nextData });
      }

      return !!record;
    },

    update: async (id, v) => {
      const record = await service.update(id, v);

      if (record) {
        const nextData = get().data.map((d) =>
          d.id === record.id ? record : d
        );

        set({ data: nextData });
      }

      return !!record;
    },

    setFilters: (v) => {
      if (!v) {
        // reset it entirely
        set({ filters: {} });
        return;
      }

      // merge the key(s) that changed
      const current = get().filters;
      set({ filters: { ...current, ...v } });
    },

    createUser: async (investorID, v) => {
      const record = await service.createUser(investorID, v);

      if (record?.investorPrivate) {
        const nextData = get().data.map((d) =>
          d.id === record.investorPrivate.id ? record.investorPrivate : d
        );

        set({ data: nextData });
      }

      return !!record;
    },
  };
});

export default useInvestorsStore;
